/**
 * Puzzle 4 — Sports Archive Crossword (data-driven).
 * Across/down placements come from the same generateLayout
 * algorithm used by adventurous_ride CrosswordStage1.
 */

import { generateLayout, getWordStartPositions } from "./crosswordLayout";

/** Base points written to DB `stage4` on success (−5 per hint used). */
export const CROSSWORD_POINTS = 100;

/** Points deducted from stage score for each crossword hint. */
export const CROSSWORD_HINT_PENALTY = 5;

/** Puzzle 5 hotspot unlocked after crossword success. */
export const CROSSWORD_UNLOCKS = "trophy_vault";

/**
 * @typedef {'across'|'down'} CrosswordDir
 * @typedef {{
 *   id: string,
 *   answer: string,
 *   clue: string,
 *   row: number,
 *   col: number,
 *   dir: CrosswordDir,
 *   number: number,
 * }} CrosswordWord
 */

/** Source bank — orientation/positions are assigned by generateLayout. */
const CROSSWORD_BANK = [
  {
    id: "trophy",
    answer: "TROPHY",
    clue: "Prize lifted by champions after a final",
  },
  {
    id: "hockey",
    answer: "HOCKEY",
    clue: "Stick-and-ball sport long tied to India's golden era",
  },
  {
    id: "india",
    answer: "INDIA",
    clue: "Nation whose sporting legacy you are restoring",
  },
  {
    id: "gold",
    answer: "GOLD",
    clue: "Colour of the highest Olympic medal",
  },
  {
    id: "dhoni",
    answer: "DHONI",
    clue: "Captain who finished off in style in 2011",
  },
  {
    id: "chess",
    answer: "CHESS",
    clue: "Board game of Anand, Praggnanandhaa and Gukesh",
  },
  {
    id: "javelin",
    answer: "JAVELIN",
    clue: "Event that made Neeraj Chopra an Olympic champion",
  },
  {
    id: "medal",
    answer: "MEDAL",
    clue: "Award hung around an athlete's neck on the podium",
  },
];

/**
 * Build CROSSWORD_WORDS via adventurous_ride-style layout generation.
 * Answers stay uppercase for the stage fill/solution maps.
 * @returns {CrosswordWord[]}
 */
export function buildCrosswordWords(bank = CROSSWORD_BANK) {
  const layout = generateLayout(
    bank.map((w) => ({
      answer: w.answer,
      clue: w.clue,
      orientation: "none",
    }))
  );

  const placed = Object.values(layout.result || {}).filter(
    (w) => w.orientation && w.orientation !== "none"
  );
  const startNumbers = getWordStartPositions(placed);

  /** Match bank entries back by answer (layout lowercases answers). */
  const byAnswer = Object.fromEntries(
    bank.map((w) => [String(w.answer).toLowerCase(), w])
  );

  const words = placed.map((w) => {
    const src = byAnswer[String(w.answer).toLowerCase()];
    const startKey = `${w.starty},${w.startx}`;
    return {
      id: src?.id || String(w.answer).toLowerCase(),
      answer: String(src?.answer || w.answer).toUpperCase(),
      clue: src?.clue || w.clue,
      row: w.starty - 1,
      col: w.startx - 1,
      dir: /** @type {CrosswordDir} */ (w.orientation),
      number: startNumbers[startKey] ?? w.position ?? 0,
    };
  });

  words.sort((a, b) => a.number - b.number || a.id.localeCompare(b.id));

  if (words.length !== bank.length) {
    console.warn(
      `[crossword] layout placed ${words.length}/${bank.length} words`
    );
  }

  return words;
}

/** @type {CrosswordWord[]} */
export const CROSSWORD_WORDS = buildCrosswordWords();

/** Build solution map + open-cell list from word placements. */
export function buildCrosswordModel(words = CROSSWORD_WORDS) {
  /** @type {Record<string, string>} */
  const solution = {};
  /** @type {Record<string, number>} */
  const numbers = {};
  let maxRow = 0;
  let maxCol = 0;

  words.forEach((w) => {
    const startKey = cellKey(w.row, w.col);
    if (numbers[startKey] == null || w.number < numbers[startKey]) {
      numbers[startKey] = w.number;
    }
    for (let i = 0; i < w.answer.length; i += 1) {
      const r = w.dir === "across" ? w.row : w.row + i;
      const c = w.dir === "across" ? w.col + i : w.col;
      const k = cellKey(r, c);
      const ch = w.answer[i];
      if (solution[k] && solution[k] !== ch) {
        throw new Error(`Crossword conflict at ${k}: ${solution[k]} vs ${ch}`);
      }
      solution[k] = ch;
      maxRow = Math.max(maxRow, r);
      maxCol = Math.max(maxCol, c);
    }
  });

  const rows = maxRow + 1;
  const cols = maxCol + 1;
  /** @type {{ row: number, col: number, number?: number }[]} */
  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const k = cellKey(r, c);
      if (!solution[k]) continue;
      cells.push({
        row: r,
        col: c,
        number: numbers[k],
      });
    }
  }

  return { rows, cols, solution, cells, numbers };
}

export function cellKey(row, col) {
  return `${row},${col}`;
}

export function parseCellKey(key) {
  const [row, col] = String(key).split(",").map(Number);
  return { row, col };
}

/** Empty player fill map for all open cells. */
export function createEmptyFill(solution) {
  return Object.fromEntries(Object.keys(solution).map((k) => [k, ""]));
}

/**
 * @param {Record<string, string>} fill
 * @param {Record<string, string>} solution
 */
export function isCrosswordSolved(fill, solution) {
  return Object.keys(solution).every(
    (k) => String(fill[k] || "").toUpperCase() === solution[k]
  );
}

/**
 * @param {Record<string, string>} fill
 * @param {Record<string, string>} solution
 * @returns {{ filled: number, total: number, correct: number, complete: boolean, solved: boolean }}
 */
export function scoreCrosswordFill(fill, solution) {
  const keys = Object.keys(solution);
  let filled = 0;
  let correct = 0;
  keys.forEach((k) => {
    const v = String(fill[k] || "").toUpperCase();
    if (v) {
      filled += 1;
      if (v === solution[k]) correct += 1;
    }
  });
  const complete = filled === keys.length;
  return {
    filled,
    total: keys.length,
    correct,
    complete,
    solved: complete && correct === keys.length,
  };
}

/** Next open cell in reading order (or direction). */
export function nextCellKey(key, solution, dir = "across", delta = 1) {
  const { row, col } = parseCellKey(key);
  const step = delta >= 0 ? 1 : -1;
  let r = row;
  let c = col;
  for (let i = 0; i < 64; i += 1) {
    if (dir === "across") c += step;
    else r += step;
    const k = cellKey(r, c);
    if (solution[k]) return k;
    if (dir === "across" && (c < 0 || c > 20)) break;
    if (dir === "down" && (r < 0 || r > 20)) break;
  }
  return key;
}

export function neighborCellKey(key, solution, direction) {
  const { row, col } = parseCellKey(key);
  const deltas = {
    ArrowLeft: [0, -1],
    ArrowRight: [0, 1],
    ArrowUp: [-1, 0],
    ArrowDown: [1, 0],
  };
  const d = deltas[direction];
  if (!d) return key;
  let r = row + d[0];
  let c = col + d[1];
  for (let i = 0; i < 12; i += 1) {
    const k = cellKey(r, c);
    if (solution[k]) return k;
    r += d[0];
    c += d[1];
  }
  return key;
}

/** Cell keys that belong to a word, in letter order. */
export function getWordCellKeys(word) {
  const keys = [];
  for (let i = 0; i < word.answer.length; i += 1) {
    const r = word.dir === "across" ? word.row : word.row + i;
    const c = word.dir === "across" ? word.col + i : word.col;
    keys.push(cellKey(r, c));
  }
  return keys;
}

/** All words that include this cell. */
export function findWordsAtCell(words, row, col) {
  return words.filter((word) => {
    for (let i = 0; i < word.answer.length; i += 1) {
      const r = word.dir === "across" ? word.row : word.row + i;
      const c = word.dir === "across" ? word.col + i : word.col;
      if (r === row && c === col) return true;
    }
    return false;
  });
}

export function findWordAtCell(words, row, col, preferDir) {
  const matches = findWordsAtCell(words, row, col);
  if (!matches.length) return null;
  if (preferDir) {
    const preferred = matches.find((w) => w.dir === preferDir);
    if (preferred) return preferred;
  }
  return matches[0];
}

/**
 * Next/prev letter key within the same word (skips locked if provided).
 * @returns {string|null}
 */
export function siblingKeyInWord(word, row, col, step, lockedKeys = {}) {
  const keys = getWordCellKeys(word);
  const current = cellKey(row, col);
  const idx = keys.indexOf(current);
  if (idx < 0) return null;
  const delta = step > 0 ? 1 : -1;
  for (let i = idx + delta; i >= 0 && i < keys.length; i += delta) {
    if (!lockedKeys[keys[i]]) return keys[i];
  }
  return null;
}

/** Prefer across if a right neighbor exists, else down. */
export function inferCellDirection(solution, row, col) {
  if (solution[cellKey(row, col + 1)]) return "across";
  if (solution[cellKey(row + 1, col)]) return "down";
  if (solution[cellKey(row, col - 1)]) return "across";
  if (solution[cellKey(row - 1, col)]) return "down";
  return "across";
}

export function isWordComplete(word, fill) {
  const keys = getWordCellKeys(word);
  return keys.every(
    (k, i) => String(fill[k] || "").toUpperCase() === word.answer[i]
  );
}

/** Reveal one empty/wrong letter in a specific word (hint). */
export function revealLetterInWord(word, fill, solution) {
  const keys = getWordCellKeys(word);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (String(fill[k] || "").toUpperCase() !== solution[k]) {
      return {
        fill: { ...fill, [k]: solution[k] },
        revealedKey: k,
      };
    }
  }
  return { fill, revealedKey: null };
}

/** Reveal one incorrect/empty cell letter (hint). */
export function applyCrosswordHint(fill, solution) {
  const keys = Object.keys(solution);
  const candidates = keys.filter(
    (k) => String(fill[k] || "").toUpperCase() !== solution[k]
  );
  if (!candidates.length) return { fill, revealedKey: null };
  const revealedKey = candidates[Math.floor(Math.random() * candidates.length)];
  return {
    fill: { ...fill, [revealedKey]: solution[revealedKey] },
    revealedKey,
  };
}
